// components/artifacts/renderers/GraphArtifactRenderer.tsx
import React, { useMemo } from "react";
import { GraphArtifact } from "@/types/Artifact";
import { parseGraphData, createChartConfig } from "@/lib/chart-utils";
import * as charts from "../charts";

interface GraphArtifactRendererProps {
  artifact: GraphArtifact;
}

export const GraphArtifactRenderer: React.FC<GraphArtifactRendererProps> = React.memo(({ 
  artifact 
}) => {
  const graphData = useMemo(() => parseGraphData(artifact.content), [artifact.content]);

  const chartSetup = useMemo(() => {
    if (!graphData || !Array.isArray(graphData.data) || graphData.data.length === 0) {
      return null;
    }
    return createChartConfig(graphData, graphData.data);
  }, [graphData]);

  if (!graphData) {
    return <div className="p-4 text-red-500">Invalid graph data format</div>;
  }

  if (!chartSetup) {
    return <div className="p-4 text-red-500">Invalid or empty data array</div>;
  }

  const { config, visualizationKeys, colors } = chartSetup;

  // All chart components now require config prop for shadcn/ui compatibility
  switch (graphData.type) {
    case "line":
      return (
        <charts.LineChartComponent
          data={graphData}
          config={config}
          visualizationKeys={visualizationKeys}
          colors={colors}
          id={artifact.id}
        />
      );
      
    case "bar":
    case "stackedBar":
    case "groupedBar":
      return (
        <charts.BarChartComponent 
          data={graphData} 
          config={config} 
          visualizationKeys={visualizationKeys} 
          colors={colors} 
          id={artifact.id} 
        />
      );

    case "area":
      return (
        <charts.AreaChartComponent 
          data={graphData} 
          config={config} 
          visualizationKeys={visualizationKeys} 
          colors={colors} 
          id={artifact.id} 
        />
      );

    case "pie":
    case "donut":
      return (
        <charts.PieChartComponent 
          data={graphData} 
          config={config} 
          colors={colors} 
          id={artifact.id} 
        />
      );
      
    case "radar":
      return (
        <charts.RadarChartComponent 
          data={graphData} 
          config={config} 
          colors={colors} 
          id={artifact.id} 
        />
      );
      
    case "funnel":
      return (
        <charts.FunnelChartComponent 
          data={graphData} 
          config={config} 
          colors={colors} 
          id={artifact.id} 
        />
      );
      
    case "scatter":
    case "bubble":
      return (
        <charts.ScatterChartComponent 
          data={graphData} 
          config={config} 
          colors={colors} 
          id={artifact.id} 
        />
      );

    case "polarArea":
      return (
        <charts.PolarAreaChartComponent 
          data={graphData} 
          config={config} 
          colors={colors} 
          id={artifact.id} 
        />
      );
    
    default:
      return (
        <div className="p-4 text-amber-600">
          Chart type "{graphData.type}" is not yet implemented. 
          Supported types: line, bar, area, pie, donut, radar, funnel, scatter, bubble, polarArea
        </div>
      );
  }
});